/**
 * Reusable condition-filter engine for data tables (monday.com-style).
 *
 * A table declares its filterable columns as `FilterColumn`s (label, type,
 * accessor). Users compose `FilterRule`s (column + operator + value) joined by
 * ONE global conjunction ('and' | 'or'), and `applyFilterRules` evaluates them
 * against the rows. Pure data/logic module — the UI lives in FilterBuilder.
 */

export type FilterColumnType = "text" | "number" | "enum"

export interface FilterColumnOption {
  value: string
  label: string
}

export interface FilterColumn<T> {
  key: string
  label: string
  type: FilterColumnType
  accessor: (row: T) => string | number | null | undefined
  /** Allowed values for `enum` columns (compared against the raw value). */
  options?: FilterColumnOption[]
}

export interface FilterRule {
  id: string
  columnKey: string
  operator: string
  value: string
}

export type FilterConjunction = "and" | "or"

export interface FilterOperator {
  value: string
  label: string
}

/** Operator choices per column type, in display order. */
export const OPERATORS_BY_TYPE: Record<FilterColumnType, FilterOperator[]> = {
  text: [
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "does not contain" },
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
  ],
  number: [
    { value: "eq", label: "=" },
    { value: "neq", label: "≠" },
    { value: "gt", label: ">" },
    { value: "gte", label: "≥" },
    { value: "lt", label: "<" },
    { value: "lte", label: "≤" },
  ],
  enum: [
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
  ],
}

/**
 * A rule is "active" (participates in filtering) when it points at a known
 * column, has a non-empty value, and — for number columns — the value parses
 * as a number. Incomplete rules are ignored so filtering can run live while
 * the user is still typing.
 */
export function isRuleActive<T>(
  rule: FilterRule,
  columns: FilterColumn<T>[]
): boolean {
  const column = columns.find((c) => c.key === rule.columnKey)
  if (!column) return false
  const value = rule.value.trim()
  if (!value) return false
  if (column.type === "number" && Number.isNaN(Number(value))) return false
  return true
}

/** Count of rules that currently participate in filtering (badge count). */
export function countActiveRules<T>(
  rules: FilterRule[],
  columns: FilterColumn<T>[]
): number {
  return rules.filter((rule) => isRuleActive(rule, columns)).length
}

function matchesRule<T>(
  row: T,
  rule: FilterRule,
  column: FilterColumn<T>
): boolean {
  const raw = column.accessor(row)

  if (column.type === "number") {
    const target = Number(rule.value.trim())
    const actual = typeof raw === "number" ? raw : raw != null ? Number(raw) : NaN
    // Missing/non-numeric cell values never match a numeric condition.
    if (Number.isNaN(actual)) return false
    switch (rule.operator) {
      case "eq":
        return actual === target
      case "neq":
        return actual !== target
      case "gt":
        return actual > target
      case "gte":
        return actual >= target
      case "lt":
        return actual < target
      case "lte":
        return actual <= target
      default:
        return true
    }
  }

  if (column.type === "enum") {
    const actual = raw == null ? "" : String(raw)
    switch (rule.operator) {
      case "is":
        return actual === rule.value
      case "is_not":
        return actual !== rule.value
      default:
        return true
    }
  }

  // text — case-insensitive
  const actual = raw == null ? "" : String(raw).toLowerCase()
  const target = rule.value.trim().toLowerCase()
  switch (rule.operator) {
    case "contains":
      return actual.includes(target)
    case "not_contains":
      return !actual.includes(target)
    case "is":
      return actual === target
    case "is_not":
      return actual !== target
    default:
      return true
  }
}

/**
 * Evaluate the active rules against `rows`. Inactive/incomplete rules are
 * skipped; with no active rules the input array is returned unchanged.
 */
export function applyFilterRules<T>(
  rows: T[],
  rules: FilterRule[],
  columns: FilterColumn<T>[],
  conjunction: FilterConjunction
): T[] {
  const active = rules
    .filter((rule) => isRuleActive(rule, columns))
    .map((rule) => ({
      rule,
      column: columns.find((c) => c.key === rule.columnKey)!,
    }))
  if (!active.length) return rows
  return rows.filter((row) =>
    conjunction === "and"
      ? active.every(({ rule, column }) => matchesRule(row, rule, column))
      : active.some(({ rule, column }) => matchesRule(row, rule, column))
  )
}
